/* sportsbookPriority — the ONE canonical source of truth for how sportsbook inventory
   is ordered across every customer-facing feed (board, Home shelves, Event Detail
   strip, nav tiles, featured selection).

   Business rule: highest-value mainstream professional and major college sports always
   appear before lower-level / amateur / niche / challenger / ITF / esports inventory.
   Lower-priority inventory is NEVER removed — it simply ranks later.

   Everything here is pure (no React, no network) so it is fast and unit-testable.

   priority number: LOWER = higher priority (shown first). The tuple used for sorting is:
     league/sport priority  →  live-before-upcoming (within equal priority)  →  start_time  →  stable id
   so a lower-level event can never jump above a premium league merely because it starts
   earlier, while chronology is preserved inside an equal-priority bucket. */

// ---- Tier bases (lower = higher priority) -----------------------------------------------
// Tier 1 = US majors + major college; Tier 2 = premium global; Tier 3 = secondary pro;
// Tier 4 = long-tail / lower level; UNKNOWN sits just below Tier 4 (visible, late) so a
// genuinely unknown league still outranks explicitly-deprioritised Tier 5 (ITF/esports).
export const TIER = { T1: 100, T2: 200, T3: 300, T4: 400, UNKNOWN: 450, T5: 500 };

// ---- Normalizers ------------------------------------------------------------------------
export function normalizeSport(sport = "") {
  const s = String(sport || "").trim().toLowerCase();
  if (!s) return "";
  if (/^american football$|^amfootball$|gridiron/.test(s)) return "football";
  if (/^ice hockey$/.test(s)) return "hockey";
  if (/^basket$/.test(s)) return "basketball";
  if (/^mixed martial|^ufc$/.test(s)) return "mma";
  if (/^e-?sports$/.test(s)) return "esports";
  return s;
}

export function normalizeLeague(league = "") {
  return String(league || "")
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLowerCase().replace(/&/g, " and ")
    .replace(/[^a-z0-9 ]+/g, " ").replace(/\s+/g, " ").trim();
}

// Exact US-major / major-college league → rank (Tier 1). Keyed by normalized league.
const MAJOR_LEAGUE_RANK = {
  nfl: TIER.T1 + 0,
  nba: TIER.T1 + 1,
  mlb: TIER.T1 + 2,
  nhl: TIER.T1 + 3,
  ncaaf: TIER.T1 + 4, "college football": TIER.T1 + 4, cfb: TIER.T1 + 4,
  ncaab: TIER.T1 + 5, "college basketball": TIER.T1 + 5, cbb: TIER.T1 + 5, ncaam: TIER.T1 + 5,
  wnba: TIER.T1 + 6,
};

// Per-sport ordered pattern tables. First matching pattern (against the normalized league
// string) wins, so more specific patterns (e.g. "atp challenger") must precede broader ones
// ("atp"). Each entry: [regex, rank]. Rank already includes its tier base.
// Soccer patterns are COUNTRY/COMPETITION-ANCHORED so lower divisions / women / youth variants that
// share a name ("Ireland - Premier League", "Spain - La Liga 2", "Germany - Bundesliga Women") are NOT
// mistaken for the real top flight. The data arrives as "Country - Competition" → normalized to
// "country competition". Exact/anchored matches only; everything else falls to the long-tail fallback.
const SOCCER_PATTERNS = [
  // Tier 2 — premium global (top flights + marquee international)
  [/^uefa champions league$|^champions league$/, TIER.T2 + 0],
  [/^england premier league$|^english premier league$|^premier league$|^epl$/, TIER.T2 + 1],
  [/^spain la liga$|^spain primera division$|^la liga$/, TIER.T2 + 2],
  [/^germany bundesliga$|^bundesliga$/, TIER.T2 + 3],
  [/^italy serie a$/, TIER.T2 + 4],
  [/^france ligue 1$|^ligue 1$/, TIER.T2 + 5],
  [/^(usa|united states) (mls|major league soccer)$|^mls$|^major league soccer$/, TIER.T2 + 6],
  [/nations league$|^fifa world cup$|^world cup$|european championship|^uefa euro|copa america|copa libertadores/, TIER.T2 + 7],
  // Tier 3 — secondary professional
  [/liga mx$|^mexico liga mx|^mexico primera/, TIER.T3 + 0],
  [/^uefa (europa|conference) league$/, TIER.T3 + 1],
  [/^netherlands eredivisie$|^eredivisie$|^portugal primeira liga$|^primeira liga$/, TIER.T3 + 2],
  [/^brazil serie a$|^argentina primera division$|^argentina liga profesional/, TIER.T3 + 3],
  [/^england championship$|^efl championship$/, TIER.T3 + 4],
];

const TENNIS_PATTERNS = [
  [/challenger/, TIER.T4 + 0],                                         // ATP/WTA Challenger → long-tail
  [/\bitf\b|\butr\b|futures|amateur/, TIER.T5 + 0],                    // ITF / UTR / amateur → lowest
  [/grand slam|australian open|roland garros|french open|wimbledon|us open/, TIER.T2 + 20],
  [/masters|atp 1000|wta 1000|atp finals|wta finals/, TIER.T2 + 21],
  [/\batp\b|\bwta\b/, TIER.T2 + 22],                                   // main tour
];

const BASKETBALL_PATTERNS = [
  [/euroleague|eurocup/, TIER.T3 + 10],
  [/\bacb\b|liga endesa|spain .*(acb|liga endesa)/, TIER.T3 + 11],
  [/\bnbl\b|australia .*nbl/, TIER.T3 + 12],
  [/\bbbl\b|germany .*(bbl|pro a)/, TIER.T3 + 13],
  [/vtb|lnb pro a|france .*(pro a|lnb)/, TIER.T3 + 14],
];

const BASEBALL_PATTERNS = [
  [/\bnpb\b|japan .*(npb|baseball)|nippon/, TIER.T3 + 20],
  [/\bkbo\b|korea .*(kbo|baseball)/, TIER.T3 + 21],
];

const FOOTBALL_PATTERNS = [
  [/\bcfl\b|canadian football/, TIER.T3 + 30],
  [/\bufl\b|\bxfl\b/, TIER.T4 + 30],
];

// Whole-sport fallbacks when no league pattern matches. Keyed by normalized sport.
// These place a sport's long-tail inventory at a sensible tier so it stays visible but late.
const SPORT_FALLBACK_RANK = {
  football: TIER.T4 + 90,     // non-NFL/NCAAF football (rare)
  basketball: TIER.T4 + 20,   // international / lower basketball
  baseball: TIER.T4 + 21,     // minor baseball
  hockey: TIER.T4 + 22,       // non-NHL hockey (KHL/SHL/Liiga etc.)
  soccer: TIER.T4 + 40,       // long-tail soccer competitions
  tennis: TIER.T4 + 50,       // uncategorised tennis
  golf: TIER.T3 + 40,         // non-PGA pro golf
  mma: TIER.T3 + 50,          // UFC / major MMA (secondary pro per spec)
  boxing: TIER.T3 + 51,       // major boxing
  esports: TIER.T5 + 50,      // esports → lowest display priority
  cricket: TIER.T4 + 60,
  rugby: TIER.T4 + 61,
  other: TIER.UNKNOWN,
};

const GOLF_PATTERNS = [
  [/pga tour|\bpga\b|the masters|us open golf|the open championship|pga championship|liv golf/, TIER.T2 + 25],
];

const MMA_PATTERNS = [
  [/\bufc\b|\bpfl\b|bellator/, TIER.T3 + 50],
];

const BOXING_PATTERNS = [
  [/.*/, TIER.T3 + 51],
];

function matchPatterns(patterns, nl) {
  for (const [re, rank] of patterns) if (re.test(nl)) return rank;
  return null;
}

/* getLeaguePriority — the canonical rank for a (league, sport) pair. Lower = higher priority. */
export function getLeaguePriority(league = "", sport = "") {
  const nl = normalizeLeague(league);
  const ns = normalizeSport(sport);

  // 1) Exact US-major / major-college league wins outright.
  if (nl && MAJOR_LEAGUE_RANK[nl] != null) return MAJOR_LEAGUE_RANK[nl];
  // token-level major check (handles e.g. "NFL Preseason" / league carrying a suffix)
  if (nl) {
    for (const key of ["nfl", "nba", "mlb", "nhl", "wnba", "ncaaf", "ncaab"]) {
      const re = new RegExp(`\\b${key}\\b`);
      if (re.test(nl) && MAJOR_LEAGUE_RANK[key] != null) return MAJOR_LEAGUE_RANK[key];
    }
  }

  // 2) Sport-specific league pattern tables.
  let r = null;
  if (ns === "soccer") r = matchPatterns(SOCCER_PATTERNS, nl);
  else if (ns === "tennis") r = matchPatterns(TENNIS_PATTERNS, nl);
  else if (ns === "basketball") r = matchPatterns(BASKETBALL_PATTERNS, nl);
  else if (ns === "baseball") r = matchPatterns(BASEBALL_PATTERNS, nl);
  else if (ns === "football") r = matchPatterns(FOOTBALL_PATTERNS, nl);
  else if (ns === "golf") r = matchPatterns(GOLF_PATTERNS, nl);
  else if (ns === "mma") r = matchPatterns(MMA_PATTERNS, nl);
  else if (ns === "boxing") r = matchPatterns(BOXING_PATTERNS, nl);
  if (r != null) return r;

  // 3) Whole-sport fallback (keeps long-tail visible but late).
  if (ns && SPORT_FALLBACK_RANK[ns] != null) return SPORT_FALLBACK_RANK[ns];

  // 4) Genuinely unknown → safe default (visible, just above explicit Tier 5).
  return TIER.UNKNOWN;
}

/* getSportPriority — rank for a sport-level nav tile (no specific league). Mirrors the
   league table so the sport tiles slot in the same order as their leagues. */
export function getSportPriority(sport = "") {
  const ns = normalizeSport(sport);
  if (SPORT_FALLBACK_RANK[ns] != null) return SPORT_FALLBACK_RANK[ns];
  return TIER.UNKNOWN;
}

/* getEventPriority — canonical rank for a single event object. */
export function getEventPriority(event = {}) {
  return getLeaguePriority(event.league, event.sport);
}

/* isPremierLeague — convenience predicate: is this Tier-1 or Tier-2 (premium) inventory? */
export function isPremierLeague(league = "", sport = "") {
  return getLeaguePriority(league, sport) < TIER.T3;
}

// ---- Deterministic sorting --------------------------------------------------------------
function startMs(e) {
  const t = Date.parse(e && e.start_time ? e.start_time : "");
  return Number.isFinite(t) ? t : Number.MAX_SAFE_INTEGER;
}
function liveRank(e) {
  return (e && e.status === "live") ? 0 : 1;   // live before upcoming inside equal priority
}
function idKey(e) {
  return String((e && (e.id ?? e.external_id)) || "");
}

/* compareEvents — the canonical comparator: priority → live → start_time → stable id. */
export function compareEvents(a, b) {
  const pa = getEventPriority(a), pb = getEventPriority(b);
  if (pa !== pb) return pa - pb;
  const la = liveRank(a), lb = liveRank(b);
  if (la !== lb) return la - lb;
  const sa = startMs(a), sb = startMs(b);
  if (sa !== sb) return sa - sb;
  const ia = idKey(a), ib = idKey(b);
  return ia < ib ? -1 : ia > ib ? 1 : 0;
}

/* sortSportsbookEvents — a new priority-ordered array (does not mutate the input). */
export function sortSportsbookEvents(events = []) {
  return [...(events || [])].sort(compareEvents);
}

// ---- Time-relevance ("starting soon") --------------------------------------------------
/* hoursUntil — hours from `now` to the event's start (negative if already started). Infinity when the
   start time is unknown (so it is never treated as "soon"). */
export function hoursUntil(event, now = Date.now()) {
  const t = startMs(event);
  if (t === Number.MAX_SAFE_INTEGER) return Infinity;
  return (t - now) / 3600000;
}

/* isStartingSoon — an UPCOMING game starting within `withinHours` (default 24h ≈ "today / tonight").
   Only a tiny negative grace (≈10 min) for clock skew — a game that started long ago but is still
   stuck "open" is NOT "starting soon". This is a TIME test, independent of league tier, so an imminent
   non-major game qualifies just like a major one. */
export function isStartingSoon(event, withinHours = 24, now = Date.now()) {
  const h = hoursUntil(event, now);
  return h <= withinHours && h >= -0.17;
}

/* sortStartingSoon — order a "starting soon" shelf by IMMINENCE first (soonest start first), with
   league priority only as a tiebreaker among games starting at (about) the same time. This is what the
   business rule asks for: major leagues lead, BUT a non-major game that is starting soon still shows —
   it is not buried behind a major league that is days away. Does not mutate the input. */
export function sortStartingSoon(events = []) {
  return [...(events || [])].sort((a, b) => {
    const la = liveRank(a), lb = liveRank(b);
    if (la !== lb) return la - lb;                 // any live game first
    const sa = startMs(a), sb = startMs(b);
    if (sa !== sb) return sa - sb;                 // then soonest-starting
    const pa = getEventPriority(a), pb = getEventPriority(b);
    if (pa !== pb) return pa - pb;                 // tie on time -> premium league first
    const ia = idKey(a), ib = idKey(b);
    return ia < ib ? -1 : ia > ib ? 1 : 0;
  });
}

/* hasStartedPregame — true when a game has already kicked off but is NOT live (still "open"/"paused"
   with no live feed). Its pre-game price is no longer valid, so the board should lock it (mirrors the
   backend wagerability gate). `graceMin` is a small clock-skew margin. */
export function hasStartedPregame(event, graceMin = 3, now = Date.now()) {
  if (!event || event.status === "live") return false;
  if (!["open", "paused"].includes(event.status)) return false; // settled/closed handled elsewhere
  const h = hoursUntil(event, now);
  return Number.isFinite(h) && h <= -(graceMin / 60);
}

/* startingSoonList — the ready-to-render list for a "Starting soon" shelf: upcoming games starting
   within the window, soonest first. Falls back to the nearest upcoming games when nothing is within
   the window, so the shelf is never empty while games exist. */
export function startingSoonList(events = [], withinHours = 24, now = Date.now()) {
  const upcoming = (events || []).filter((e) => e && (e.status === "open" || e.status === "paused"));
  const soon = upcoming.filter((e) => isStartingSoon(e, withinHours, now));
  return sortStartingSoon(soon.length ? soon : upcoming);
}

/* sortLeagueBuckets — order league buckets by their league priority, then by the earliest
   event inside, then by league name. Each bucket: { league, sport, events: [...] }.
   Does not mutate the input array; returns a new ordered array. */
export function sortLeagueBuckets(buckets = []) {
  const rankOf = (b) => {
    // Prefer an explicit league/sport on the bucket; else derive from its first event.
    if (b.league || b.sport) {
      const lp = getLeaguePriority(b.league, b.sport);
      // if the bucket's declared league is generic, also consider its events' best rank
      const evBest = (b.events || []).reduce((m, e) => Math.min(m, getEventPriority(e)), Infinity);
      return Math.min(lp, Number.isFinite(evBest) ? evBest : lp);
    }
    return (b.events || []).reduce((m, e) => Math.min(m, getEventPriority(e)), TIER.UNKNOWN);
  };
  const earliest = (b) => (b.events || []).reduce((m, e) => Math.min(m, startMs(e)), Number.MAX_SAFE_INTEGER);
  return [...(buckets || [])].sort((a, b) => {
    const ra = rankOf(a), rb = rankOf(b);
    if (ra !== rb) return ra - rb;
    const ea = earliest(a), eb = earliest(b);
    if (ea !== eb) return ea - eb;
    const na = normalizeLeague(a.league), nb = normalizeLeague(b.league);
    return na < nb ? -1 : na > nb ? 1 : 0;
  });
}
